/**
 * Tabs atom — controlled/uncontrolled tabs z ARIA role="tablist" + keyboard nav.
 *
 * Usage:
 *   <Tabs
 *     defaultValue="overview"
 *     items={[
 *       { value: 'overview', label: 'Overview', content: <OverviewTab/> },
 *       { value: 'modules', label: 'Moduły', content: <ModulesTab/> },
 *     ]}
 *   />
 *
 * Controlled:
 *   <Tabs value={active} onChange={setActive} items={...} />
 *
 * Keyboard: ArrowLeft/ArrowRight + Home/End między trigger-ami.
 * Lazy content: renderujemy tylko aktywny panel (pozostale unmount).
 */

import { type KeyboardEvent, type ReactNode, useCallback, useRef, useState } from 'react';

export interface TabsItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  /** Opcjonalny `data-testid` na przycisku zakładki (role="tab"). */
  testId?: string;
}

export interface TabsProps {
  items: TabsItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  idPrefix?: string;
}

export function Tabs({
  items,
  value,
  defaultValue,
  onChange,
  className,
  idPrefix = 'tabs',
}: TabsProps) {
  const [internal, setInternal] = useState<string>(() => {
    if (defaultValue) return defaultValue;
    return items.find((i) => !i.disabled)?.value ?? items[0]?.value ?? '';
  });

  const active = value ?? internal;
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activate = useCallback(
    (next: string) => {
      if (value === undefined) setInternal(next);
      onChange?.(next);
    },
    [value, onChange],
  );

  const focusTrigger = useCallback(
    (fromIndex: number, direction: 1 | -1 | 'home' | 'end') => {
      let target = fromIndex;
      if (direction === 'home') target = 0;
      else if (direction === 'end') target = items.length - 1;
      else {
        for (let i = 0; i < items.length; i += 1) {
          target = (target + direction + items.length) % items.length;
          if (!items[target]?.disabled) break;
        }
      }
      const el = triggerRefs.current[target];
      if (el) {
        el.focus();
        const item = items[target];
        if (item) activate(item.value);
      }
    },
    [items, activate],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusTrigger(index, 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusTrigger(index, -1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTrigger(index, 'home');
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTrigger(index, 'end');
    }
  };

  const activePanel = items.find((i) => i.value === active);

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-orientation="horizontal"
        className="flex flex-wrap gap-1 border-b border-border-secondary"
      >
        {items.map((item, index) => {
          const selected = item.value === active;
          return (
            <button
              key={item.value}
              ref={(el) => {
                triggerRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${idPrefix}-trigger-${item.value}`}
              aria-selected={selected}
              // Bez treści panelu nie ma — nie wskazujemy czytnikom ekranu nieistniejącego elementu.
              aria-controls={item.content != null ? `${idPrefix}-panel-${item.value}` : undefined}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => !item.disabled && activate(item.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-testid={item.testId}
              className={[
                '-mb-px inline-flex h-10 items-center border-b-2 px-4 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600',
                selected
                  ? 'border-accent-600 text-accent-600 dark:text-accent-300'
                  : 'border-transparent text-text-secondary hover:border-border-primary hover:text-text-primary',
                item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              ].join(' ')}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {/* Bez treści (`content: null`) — sam pasek zakładek, bez pustego panelu (ekrany, które
          rysują treść pod spodem same, np. Centrum uprawnień / Typy dokumentów). */}
      {activePanel && activePanel.content != null && (
        <div
          role="tabpanel"
          id={`${idPrefix}-panel-${activePanel.value}`}
          aria-labelledby={`${idPrefix}-trigger-${activePanel.value}`}
          className="pt-4"
        >
          {activePanel.content}
        </div>
      )}
    </div>
  );
}
